import type {

  CourseDetailDto,

  LevelDetailDto,

  LessonSummaryDto,

  TaggedContentBlockDto,

  TaggedContentLessonDto

} from '../services/contentService';

import { ContentBlockType } from '../services/contentService';

import type { AppLocale } from '../i18n';

import { pickBlockTitle, pickLocalized, pickLocalizedDescription, pickLocalizedTitle } from './localizedContent';
import { buildTagBlockLessons, isTagBlockLessonComplete, type TagBlockLesson } from './tagBlockLessons';



export type LessonMapItemKind = 'level' | 'lesson' | 'block';



export interface LessonMapItem {

  id: string;

  kind: LessonMapItemKind;

  entityId: number;

  title: string;

  subtitle?: string;

  order: number;

  isCompleted: boolean;

  isCurrent: boolean;

  /** Gated by subscription or course/level access rules. */

  isLocked?: boolean;

  subscriptionPlanIds?: number[];

  /** Course lesson navigation */

  lessonId?: number;

  /** Tag block navigation index in full ordered list */

  blockIndex?: number;

  /** Tag lesson navigation index in full ordered list */

  lessonIndex?: number;

  /** Catalog flow kind (`course` | `level` | `lesson`) for quiz/exercise maps. */

  flowKind?: string;

}



export interface LessonMapSection {

  levelId?: number;

  levelTitle?: string;

  levelCompleted?: boolean;

  levelLocked?: boolean;

  levelSubscriptionPlanIds?: number[];

  /**
   * Visual nesting for catalog flow maps (course → level → lesson).
   * Lesson maps leave this unset (treated as a level group).
   */
  sectionKind?: 'course' | 'level' | 'lesson';

  items: LessonMapItem[];

  /** Nested subsections (e.g. levels under a course, lessons under a level). */
  children?: LessonMapSection[];

}



export interface LessonMapData {

  title: string;

  subtitle?: string;

  sections: LessonMapSection[];

}



function orderedLevelLessons(level: LevelDetailDto): LessonSummaryDto[] {

  if (level.items?.length) {

    return level.items

      .filter((i) => i.type === 'lesson')

      .sort((a, b) => a.displayOrder - b.displayOrder)

      .map((i) => {

        const fromData = i.data as LessonSummaryDto | undefined;

        return fromData ?? level.lessons.find((l) => l.id === i.id);

      })

      .filter((l): l is LessonSummaryDto => !!l);

  }

  return [...(level.lessons ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);

}



function orderedCourseLevels(course: CourseDetailDto): LevelDetailDto[] {

  if (course.items?.length) {

    return course.items

      .filter((i) => i.type === 'level')

      .sort((a, b) => a.displayOrder - b.displayOrder)

      .map((i) => {

        const fromData = i.data as LevelDetailDto | undefined;

        return fromData ?? course.levels.find((l) => l.id === i.id);

      })

      .filter((l): l is LevelDetailDto => !!l);

  }

  return [...course.levels].sort((a, b) => a.displayOrder - b.displayOrder);

}



export function buildCourseLessonMap(

  course: CourseDetailDto,

  currentLessonId: number,

  locale: AppLocale = 'en'

): LessonMapData {

  const sections: LessonMapSection[] = [];



  for (const level of orderedCourseLevels(course)) {

    const lessons = orderedLevelLessons(level);

    if (lessons.length === 0) continue;



    const levelLocked = level.isAccessible === false;



    const items: LessonMapItem[] = lessons.map((lesson, idx) => ({

      id: `lesson-${lesson.id}`,

      kind: 'lesson',

      entityId: lesson.id,

      title: pickLocalizedTitle(lesson, locale),

      subtitle: pickLocalizedDescription(lesson, locale) || undefined,

      order: lesson.displayOrder ?? idx,

      isCompleted: lesson.isCompleted === true,

      isCurrent: lesson.id === currentLessonId,

      isLocked: levelLocked || lesson.isAccessible === false,

      subscriptionPlanIds: lesson.subscriptionPlanIds,

      lessonId: lesson.id

    }));



    sections.push({

      levelId: level.id,

      levelTitle: pickLocalizedTitle(level, locale),

      levelCompleted: level.isCompleted === true,

      levelLocked,

      levelSubscriptionPlanIds: level.subscriptionPlanIds,

      items

    });

  }



  return {

    title: 'Lesson map',

    subtitle: pickLocalizedTitle(course, locale),

    sections

  };

}



function blockTitle(block: TaggedContentBlockDto, locale: AppLocale): string {

  const localized = pickBlockTitle(block, locale).trim();

  if (localized) return localized;

  const lessonTitle = pickLocalized(block.lessonTitle, block.lessonTitleFa, locale).trim();

  if (lessonTitle) return lessonTitle;

  return `Block ${block.displayOrder + 1}`;

}



function blockSubtitle(block: TaggedContentBlockDto, locale: AppLocale): string | undefined {

  const lessonTitle = pickLocalized(block.lessonTitle, block.lessonTitleFa, locale).trim();

  return lessonTitle || undefined;

}



function shouldGroupTagByLevel(

  entries: Array<{ levelId: number; levelTitle?: string }>

): boolean {

  const ids = new Set(entries.map((e) => e.levelId).filter((id) => id > 0));

  return ids.size > 1;

}



function tagBlockLessonMapItem(
  lesson: TagBlockLesson,
  index: number,
  currentBlockId: number | undefined,
  locale: AppLocale
): LessonMapItem {
  const anchor = lesson.blocks[0];
  if (!anchor) {
    return {
      id: lesson.id,
      kind: 'block',
      entityId: 0,
      title: 'Lesson',
      order: index,
      isCompleted: false,
      isCurrent: false,
      blockIndex: index
    };
  }
  return {
    id: lesson.id,
    kind: 'block',
    entityId: anchor.id,
    title: blockTitle(anchor, locale),
    subtitle: blockSubtitle(anchor, locale),
    order: anchor.displayOrder ?? index,
    isCompleted: isTagBlockLessonComplete(lesson),
    isCurrent: lesson.blocks.some((block) => block.id === currentBlockId),
    blockIndex: index
  };
}

export function buildTagBlockLessonMap(
  blocks: TaggedContentBlockDto[],
  currentBlockId: number | undefined,
  locale: AppLocale = 'en'
): LessonMapData {
  const lessons = buildTagBlockLessons(blocks);
  const anchors = lessons.flatMap((lesson) => {
    const anchor = lesson.blocks[0];
    return anchor ? [anchor] : [];
  });
  const groupByLevel = shouldGroupTagByLevel(anchors);

  if (!groupByLevel) {
    return {
      title: 'Lesson map',
      sections: [{ items: lessons.map((lesson, index) => tagBlockLessonMapItem(lesson, index, currentBlockId, locale)) }]
    };
  }

  const byLevel = new Map<number, { title: string; titleFa?: string; lessons: Array<{ lesson: TagBlockLesson; index: number }> }>();
  lessons.forEach((lesson, index) => {
    const anchor = lesson.blocks[0];
    if (!anchor) return;
    const key = anchor.levelId || 0;
    const entry = { lesson, index };
    const existing = byLevel.get(key);
    if (existing) existing.lessons.push(entry);
    else {
      byLevel.set(key, {
        title: anchor.levelTitle || 'Level',
        titleFa: anchor.levelTitleFa,
        lessons: [entry]
      });
    }
  });

  const sections: LessonMapSection[] = [];
  for (const [levelId, group] of byLevel) {
    const items = group.lessons.map(({ lesson, index }) => tagBlockLessonMapItem(lesson, index, currentBlockId, locale));
    sections.push({
      levelId: levelId || undefined,
      levelTitle: pickLocalized(group.title, group.titleFa, locale),
      levelCompleted: items.length > 0 && items.every((item) => item.isCompleted),
      items
    });
  }

  return { title: 'Lesson map', sections };
}


export function buildTagLessonsMap(

  lessons: TaggedContentLessonDto[],

  currentLessonId: number | undefined,

  locale: AppLocale = 'en'

): LessonMapData {

  const ordered = [...lessons].sort((a, b) => a.displayOrder - b.displayOrder);

  const groupByLevel = shouldGroupTagByLevel(ordered);



  if (!groupByLevel) {

    const items: LessonMapItem[] = ordered.map((lesson, index) => ({

      id: `lesson-${lesson.id}`,

      kind: 'lesson',

      entityId: lesson.id,

      title: pickLocalizedTitle(lesson, locale),

      subtitle: pickLocalizedDescription(lesson, locale) || undefined,

      order: lesson.displayOrder ?? index,

      isCompleted: lesson.isPassedForTag === true,

      isCurrent: lesson.id === currentLessonId,

      lessonId: lesson.id,

      lessonIndex: index

    }));

    return { title: 'Lesson map', sections: [{ items }] };

  }



  const byLevel = new Map<number, { title: string; lessons: TaggedContentLessonDto[] }>();

  for (const lesson of ordered) {

    const key = lesson.levelId || 0;

    const existing = byLevel.get(key);

    if (existing) {

      existing.lessons.push(lesson);

    } else {

      byLevel.set(key, { title: lesson.levelTitle || 'Level', lessons: [lesson] });

    }

  }



  const sections: LessonMapSection[] = [];

  const sortedLevels = [...byLevel.entries()].sort((a, b) => {

    const orderA = a[1].lessons[0]?.displayOrder ?? 0;

    const orderB = b[1].lessons[0]?.displayOrder ?? 0;

    return orderA - orderB;

  });



  for (const [levelId, group] of sortedLevels) {

    const items: LessonMapItem[] = group.lessons.map((lesson) => {

      const lessonIndex = ordered.findIndex((l) => l.id === lesson.id);

      return {

        id: `lesson-${lesson.id}`,

        kind: 'lesson',

        entityId: lesson.id,

        title: pickLocalizedTitle(lesson, locale),

        subtitle: pickLocalizedDescription(lesson, locale) || undefined,

        order: lesson.displayOrder ?? lessonIndex,

        isCompleted: lesson.isPassedForTag === true,

        isCurrent: lesson.id === currentLessonId,

        lessonId: lesson.id,

        lessonIndex

      };

    });

    const allPassed = items.length > 0 && items.every((i) => i.isCompleted);

    sections.push({

      levelId: levelId || undefined,

      levelTitle: group.title,

      levelCompleted: allPassed,

      items

    });

  }



  return { title: 'Lesson map', sections };

}

